// src/pages/AdvertiserDashboard.jsx
import React, { useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  Legend,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { jwtDecode } from "jwt-decode";
import TableExporterImporter from "../components/TableExporterImporter";

import config from "../config";

const baseUrl = config.baseURL;
const getAdvertiserIdFromToken = () => {
  try {
    const token = localStorage.getItem("token"); // or sessionStorage
    if (!token) return null;
    const decoded = jwtDecode(token);
    console.log("🔑 Decoded token:", decoded);
    return decoded.advertiserId || decoded.sub || null;
  } catch (err) {
    console.error("❌ Failed to decode token", err);
    return null;
  }
};
// Fetch function for aggregate reports
const fetchAggregate = async ({ queryKey }) => {
  const [_key, advertiserId, adId, start, end, granularity] = queryKey;

  if (!advertiserId) {
    console.warn("⚠️ No advertiserId provided");
    return { kpi: {}, dailySeries: [], zonesBreakdown: [], ads: [] };
  }

  const url = new URL(`${baseUrl}/api/ads-aggregate`);
  url.searchParams.set("advertiserId", advertiserId);
  if (start) url.searchParams.set("start", start);
  if (end) url.searchParams.set("end", end);
  if (adId && adId !== "all") url.searchParams.set("adId", adId);
  if (granularity) url.searchParams.set("granularity", granularity);

  console.log("➡️ Fetching aggregate reports with URL:", url.toString());
  console.log("url", url);

  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Failed to fetch reports: ${res.status} ${txt}`);
  }
  console.log("res", res);

  const data = await res.json();
  console.log("✅ Parsed response data:", data);
  return data;
};

// CSV helper
const toCSV = (rows, cols) => {
  const header = cols.map((c) => `"${c.label.replace(/"/g, '""')}"`).join(",");
  const lines = rows.map((r) =>
    cols
      .map((c) => {
        const v = typeof c.key === "function" ? c.key(r) : r[c.key];
        return `"${String(v ?? "").replace(/"/g, '""')}"`;
      })
      .join(",")
  );
  return [header, ...lines].join("\n");
};

// Sparkline small chart
const Sparkline = ({ series = [], color = "#1976d2" }) => {
  const data = (series || []).map((d) => ({
    x: d.date,
    y: d.impressions || 0,
  }));
  const fallback = data.length
    ? data
    : [
        { x: "0", y: 0 },
        { x: "1", y: 0 },
      ];
  return (
    <div style={{ width: 120, height: 36 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={fallback}>
          <Line dataKey="y" stroke={color} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const AdvertiserDashboard = () => {
  const advertiserId = useMemo(() => getAdvertiserIdFromToken(), []);

  const [start, setStart] = useState(dayjs().startOf("month"));
  const [end, setEnd] = useState(dayjs().endOf("month"));
  const [selectedAd, setSelectedAd] = useState("all");
  const [granularity, setGranularity] = useState("day");

  const formattedStart = start.format("YYYY-MM-DD");
  const formattedEnd = end.format("YYYY-MM-DD");

  // React Query
  const queryKey = [
    "reportsAggregate",
    advertiserId,
    selectedAd,
    formattedStart,
    formattedEnd,
    granularity,
  ];
  console.log("advertiserId", advertiserId);
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: fetchAggregate,
    enabled: Boolean(advertiserId),
    keepPreviousData: true,
    staleTime: 60 * 1000,
  });

  const payload = data?.success
    ? data
    : { kpi: {}, dailySeries: [], zonesBreakdown: [], ads: [] };

  const kpi = payload.kpi || {};
  const dailySeries = payload.dailySeries || [];
  const zones = payload.zones || payload.zonesBreakdown || [];
  const ads = payload.ads || [];

  // KPI numbers
  const totalImpressions = kpi.impressions || 0;
  const totalClicks = kpi.clicks || 0;
  const totalLeads = kpi.leads || 0;
  const ctrPct = (kpi.ctr || 0) * 100;
  const remainingBudget = kpi.remaining_budget || 0;

  // Funnel
  const reach = totalImpressions;
  const clicksCount = totalClicks;
  const leadsCount = totalLeads;

  // Export CSV
  const handleExportCSV = useCallback(() => {
    const cols = [
      { key: "ad_id", label: "Ad ID" },
      { key: "title", label: "Title" },
      { key: "impressions", label: "Impressions" },
      { key: "clicks", label: "Clicks" },
      { key: (r) => `${((r.ctr || 0) * 100).toFixed(2)}%`, label: "CTR" },
      { key: "current_spend", label: "Spend" },
      {
        key: (r) => (r.zones ? r.zones.map((z) => z.name).join("|") : ""),
        label: "Zones",
      },
    ];
    const csv = toCSV(ads, cols);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ads_report_${advertiserId}_${formattedStart}_${formattedEnd}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [ads, advertiserId, formattedStart, formattedEnd]);

  const handleExportPDF = useCallback(() => {
    alert("PDF export: integrate jsPDF or react-pdf.");
  }, []);

  const granularityLabel =
    granularity === "day"
      ? "Daily"
      : granularity === "week"
      ? "Weekly"
      : "Monthly";

  if (isLoading)
    return (
      <Box p={4}>
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  if (error)
    return (
      <Box p={4}>
        <Typography color="error">{String(error)}</Typography>
      </Box>
    );

  return (
    <Box>
      {/* Controls */}
      <Box
        mb={3}
        p={2}
        sx={{ backgroundColor: "grey.50", borderRadius: 2, boxShadow: 1 }}
      >
        <Grid container spacing={2}>
          <Grid item>
            <DatePicker
              label="Start"
              value={start}
              onChange={(v) => setStart(v || dayjs())}
            />
          </Grid>
          <Grid item>
            <DatePicker
              label="End"
              value={end}
              onChange={(v) => setEnd(v || dayjs())}
            />
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Ad</InputLabel>
              <Select
                value={selectedAd}
                label="Ad"
                onChange={(e) => setSelectedAd(e.target.value)}
              >
                <MenuItem value="all">All Ads</MenuItem>
                {ads.map((a) => (
                  <MenuItem key={a.ad_id} value={a.ad_id}>
                    {a.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 140 }}>
              <InputLabel>Granularity</InputLabel>
              <Select
                value={granularity}
                label="Granularity"
                onChange={(e) => setGranularity(e.target.value)}
              >
                <MenuItem value="day">Day</MenuItem>
                <MenuItem value="week">Week</MenuItem>
                <MenuItem value="month">Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <TableExporterImporter
              data={ads}
              onDataChange={(updated) => {
                // allow inline editing / importing to update ads
                console.log("Updated ads from importer:", updated);
              }}
              Pagetitle="Ads Performance Report"
              disableImport
            />
          </Grid>
        </Grid>
      </Box>

      {/* KPI row */}
      <Grid container spacing={2} mt={2} sx={{ width: "100%" }}>
        {[
          { label: "Impressions", value: totalImpressions.toLocaleString() },
          { label: "Clicks", value: totalClicks.toLocaleString() },
          { label: "CTR", value: `${ctrPct.toFixed(2)}%` },
          {
            label: "Spend",
            value: `$${(kpi.current_spend || 0).toFixed(2)}`,
            extra: `Remaining $${remainingBudget.toFixed(2)}`,
          },
        ].map((k, idx) => (
          <Grid item key={idx} xs={6} sm={6} md={3}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 2,
                boxShadow: 3,
                "&:hover": { boxShadow: 6, transform: "translateY(-3px)" },
                transition: "all 0.2s ease-in-out",
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", fontWeight: 600 }}
                >
                  {k.label}
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ my: 1 }}>
                  {k.value}
                </Typography>
                {k.extra && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    {k.extra}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={2} mt={2}>
        <Grid xs={12} md={8}>
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {granularityLabel} Impressions & Clicks
              </Typography>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={dailySeries}>
                  <XAxis dataKey="date" minTickGap={10} />
                  <YAxis />
                  <ReTooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="impressions"
                    stroke="#1976d2"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="clicks"
                    stroke="#2e7d32"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} md={4}>
          <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 2 }}>
            <CardContent>
              <Typography variant="h6">Zones Breakdown</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={zones} layout="vertical">
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <ReTooltip />
                  <Bar dataKey="impressions">
                    {zones.map((z, idx) => (
                      <Cell
                        key={z.zone_id}
                        fill={
                          ["#1976d2", "#00bcd4", "#ff9800", "#8e24aa"][idx % 4]
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6">Funnel</Typography>
              <Typography variant="subtitle2">
                Reach: {reach.toLocaleString()}
              </Typography>
              <Typography variant="subtitle2">
                Clicks: {clicksCount.toLocaleString()} (
                {reach ? ((clicksCount / reach) * 100).toFixed(2) : "0"}%)
              </Typography>
              <Typography variant="subtitle2">
                Leads: {leadsCount.toLocaleString()} (
                {clicksCount
                  ? ((leadsCount / clicksCount) * 100).toFixed(2)
                  : "0"}
                % from clicks)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Ads Table */}
      <Box mt={3}>
        <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="h6">Ads Performance</Typography>
              <Typography variant="caption" color="text.secondary">
                Sparkline shows impressions trend
              </Typography>
            </Box>
            <Table size="small" stickyHeader>
              <TableHead sx={{ backgroundColor: "grey.100" }}>
                <TableRow>
                  <TableCell>Preview</TableCell>
                  <TableCell>Ad</TableCell>
                  <TableCell>Impr.</TableCell>
                  <TableCell>Clicks</TableCell>
                  <TableCell>CTR</TableCell>
                  <TableCell>Spend</TableCell>
                  <TableCell>Zones</TableCell>
                  <TableCell>Trend</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ads.map((r, i) => (
                  <TableRow
                    key={r.ad_id}
                    sx={{
                      backgroundColor: i % 2 === 0 ? "grey.50" : "inherit",
                      "&:hover": { backgroundColor: "grey.100" },
                    }}
                  >
                    <TableCell>
                      {r.thumbnail ? (
                        <Avatar src={r.thumbnail} variant="rounded" />
                      ) : (
                        <Avatar>{r.title?.[0]}</Avatar>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 240,
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {r.title}
                    </TableCell>
                    <TableCell>
                      {(r.impressions || 0).toLocaleString()}
                    </TableCell>
                    <TableCell>{(r.clicks || 0).toLocaleString()}</TableCell>
                    <TableCell>{((r.ctr || 0) * 100).toFixed(2)}%</TableCell>
                    <TableCell>${(r.current_spend || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      {(r.zones || []).map((z) => (
                        <Chip
                          key={z.id}
                          label={z.name}
                          size="small"
                          sx={{ mr: 0.5 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      {r.dailySeries ? (
                        <Sparkline series={r.dailySeries} />
                      ) : (
                        <Sparkline series={[]} color="#e0e0e0" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AdvertiserDashboard;
